'use client';

import { getSession, signOut, useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useCountdown, useEventListener, useInterval } from 'usehooks-ts';
import { useRouter } from 'next/navigation';

export function useActivityListener(): {
    minutesRemaining: number;
    timeoutWarning: boolean;
} {
    const { data: session, status } = useSession();
    const router = useRouter();
    const [shouldUpdate, setShouldUpdate] = useState(false);
    const [minutesRemaining, { startCountdown, resetCountdown, stopCountdown }] = useCountdown({
        countStart: 14, // 14 minutes until we expire the session. The countdown should account for the debounced 'last active' time.
        intervalMs: 60 * 1000, // interval is in minutes, not seconds. Done to reduce spam.
    });
    const timeoutWarning = minutesRemaining < 5;

    const handleUpdate = () => {
        console.debug(`Handle timeout update. Should update: ${shouldUpdate}. Minutes remaining: ${minutesRemaining}`);
        if (typeof navigator === 'undefined' || !navigator.onLine) {
            return;
        }
        if (shouldUpdate) {
            console.info(`Updating session at ${new Date().toISOString()}`);
            setShouldUpdate(false);
            getSession()
                .then(sess => {
                    if (!sess) {
                        console.debug('getSession call found no session');
                        stopCountdown();
                        setShouldUpdate(false);
                        signOut({ callbackUrl: '/' }).finally(() => void router.push('/'));
                        return;
                    }
                    resetCountdown();
                    startCountdown();
                    console.debug(`Session updated at ${new Date().toISOString()}`);
                })
                .catch(err => {
                    console.warn(`Error updating session inside handleUpdate: ${JSON.stringify(err)}`);
                });
        }
    };
    const handleSignOut = async () => {
        return signOut({ callbackUrl: '/auth/signout', redirect: true })
            .catch(err => {
                console.error(err);
                window.location.href = '/auth/signout';
            })
            .finally(() => {
                getSession();
            });
    };
    const onActive = () => {
        if (status !== 'authenticated') {
            return;
        }
        if (!shouldUpdate) {
            setShouldUpdate(true);
        }
    };

    /* invokes the `handleUpdate` function every 1 minute */
    useInterval(handleUpdate, 1 * 60 * 1000); // 1 minute * 60 seconds * 1000 milliseconds

    /* The `useEventListener` hook cleans up after itself when the component unmounts */
    useEventListener('scroll', onActive);
    useEventListener('click', onActive);
    useEventListener('keydown', onActive);
    useEventListener('mousemove', onActive);

    /** Sign out users when 0 minutes are remaining */
    useEffect(() => {
        if (minutesRemaining === 0) {
            void handleSignOut();
        }
        return () => {
            setShouldUpdate(false);
        };
    }, [minutesRemaining]);

    /**
     * watches for changes in the session.expires value. If it changes, we know the session has been updated
     * elsewhere, so we can ignore any pending update we might have accumulated from before that update.
     */
    useEffect(() => {
        if (session?.expires) {
            console.debug(`Session expires at ${session.expires}`);
            setShouldUpdate(false);
            resetCountdown();
            startCountdown();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.expires]);

    return status === 'authenticated'
        ? { timeoutWarning, minutesRemaining }
        : { timeoutWarning: false, minutesRemaining };
}
